import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import type { BurnoutRisk, WorkoutBrief } from "../../dashboardApi";
import { ReadinessRing, ReadinessSubSignal } from "./ReadinessRing";
import { formatDuration, formatDistance } from "./SessionHero";
import { pickHeroReadout, HERO_READOUT_FALLBACK_RU, HeroTone } from "./todayHeroUtils";
import { getSportKey } from "../../workoutStatus";

/**
 * TodayHeroCard — «кабина решения» (W-REBUILD-1, мандат раздел 6.1).
 *
 * ОДИН hero-объект вместо ленты равнозначных карточек: вердикт дня
 * (человеческая строка, тон = данные) → готовность (компакт-кольцо +
 * биометрия строкой) → тренировка дня (заголовок, метрики, CTA).
 * Burnout-тренд больше не отдельный баннер на пол-экрана — он источник
 * вердикта; детали (слои сигналов) раскрываются по tap внутри hero.
 */

// Канон-сессия 2026-07-24 вывела лайм (--active-500) из роли «хорошо»:
// «ok» — это okabe #009E73 (--ok), colorblind-safe, а лайм остаётся только
// у disc-run, чтобы снять hue-коллизию дисциплины и статуса. Здесь кольцо
// готовности и HRV-дельта продолжали краситься лаймом — то есть на главном
// экране «зелёный свет» был именно тем зелёным, от которого канон уходил.
const TONE_COLOR: Record<HeroTone, string> = {
  red: "var(--danger-500)",
  amber: "var(--warning-500)",
  green: "var(--ok)",
  bright: "var(--ok)",
  muted: "var(--fg-muted)",
};

const SPORT_DOT: Record<string, string> = {
  swim: "var(--disc-swim)",
  cycle: "var(--disc-bike)",
  gravel: "var(--disc-gravel)",
  run: "var(--disc-run)",
  trail: "var(--disc-trail)",
  triathlon: "var(--disc-triathlon)",
  duathlon: "var(--disc-duathlon)",
  hyrox: "var(--disc-hyrox)",
  strength: "var(--disc-strength)",
  other: "var(--fg-muted)",
};

export interface TodayHeroCardProps {
  readinessScore: number | null;
  /** Текущий TSB — умеряет «день для качественной работы» при глубокой усталости. */
  tsb?: number | null;
  subSignals?: ReadinessSubSignal[];
  burnout: BurnoutRisk | null;
  hrv: number | null;
  hrvDelta: number | null;
  sleepHours: number | null;
  checkedInToday: boolean;
  workout: WorkoutBrief | null;
  onStart?: () => void;
  onOpenWorkout?: () => void;
  /** Extra slot rendered inside the card below the workout (protocol strip). */
  children?: React.ReactNode;
}

export function TodayHeroCard({
  readinessScore,
  tsb = null,
  subSignals,
  burnout,
  hrv,
  hrvDelta,
  sleepHours,
  checkedInToday,
  workout,
  onStart,
  onOpenWorkout,
  children,
}: TodayHeroCardProps) {
  const { t } = useTranslation("dashboard");
  const [expanded, setExpanded] = useState(false);

  const burnoutBand = burnout?.has_enough_data ? burnout.band : null;
  const readout = pickHeroReadout(
    burnoutBand === "green" ? null : burnoutBand,
    readinessScore,
    tsb,
  );
  const readoutColor = TONE_COLOR[readout.tone];
  const readoutText = t(readout.key, {
    defaultValue: HERO_READOUT_FALLBACK_RU[readout.key] ?? "",
  });

  const sportKey = getSportKey(workout?.discipline ?? null);
  const dot = SPORT_DOT[sportKey] ?? SPORT_DOT.other;
  const duration = formatDuration(
    workout?.planned_duration_minutes,
    t("common:hour_short", { defaultValue: "ч" }),
    t("common:min_short", { defaultValue: "мин" }),
  );
  const distance = formatDistance(
    workout?.planned_distance_km != null ? Number(workout.planned_distance_km) : null,
  );
  const isDone = workout?.status === "done" || workout?.status === "completed";

  // Burnout detail chips — reuse layer components, human labels only.
  const detailChips = (burnout?.layers ?? [])
    .filter((l) => l.score != null && l.score <= -0.1)
    .flatMap((l) => l.components.filter((c) => c.value != null))
    .slice(0, 6);

  return (
    <div data-testid="today-hero-card" className="indigo-card p-4 flex flex-col gap-3.5">
      {/* ── Вердикт дня ── */}
      <button
        type="button"
        data-testid="hero-readout"
        data-tone={readout.tone}
        onClick={readout.fromBurnout ? () => setExpanded((v) => !v) : undefined}
        className={`flex items-start gap-2 text-left ${readout.fromBurnout ? "" : "cursor-default"}`}
        aria-expanded={readout.fromBurnout ? expanded : undefined}
      >
        <span
          className="w-[3px] self-stretch rounded-sm shrink-0"
          style={{ backgroundColor: readoutColor }}
        />
        <span className="flex-1 text-[15px] font-semibold leading-snug text-fg-primary">
          {readoutText}
        </span>
        {readout.fromBurnout &&
          (expanded ? (
            <CaretUp className="w-4 h-4 text-fg-muted shrink-0 mt-0.5" />
          ) : (
            <CaretDown className="w-4 h-4 text-fg-muted shrink-0 mt-0.5" />
          ))}
      </button>

      {readout.fromBurnout && expanded && (
        <div data-testid="hero-readout-details" className="rounded-md bg-bg-raised px-3 py-2.5 space-y-2">
          <div className="text-[11.5px] text-fg-secondary leading-relaxed">
            {t(`burnout.body_${burnoutBand}`, {
              defaultValue: t("hero.burnout_details", { defaultValue: "Тренд последних недель: добавь лёгкие дни и следи за сном." }),
            })}
          </div>
          {detailChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {detailChips.map((c) => (
                <span
                  key={c.label}
                  className="px-2 py-1 rounded-[3px] bg-bg-surface border border-bg-border text-[10.5px] text-fg-secondary"
                >
                  {t(`burnout.signal_${c.label}`, { defaultValue: c.label })}
                </span>
              ))}
            </div>
          )}
          <Link
            to="/wellness"
            data-testid="hero-readout-wellness-cta"
            className="inline-flex px-3 py-1.5 rounded-pill bg-accent text-accent-on text-xs font-semibold hover:bg-accent-hover transition-colors"
          >
            {t("burnout.cta_wellness", { defaultValue: "Записать самочувствие" })}
          </Link>
        </div>
      )}

      {/* ── Готовность: компакт-кольцо + биометрия ── */}
      <div className="flex items-center gap-4">
        <ReadinessRing score={readinessScore} size={76} strokeWidth={8} subSignals={undefined} />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex items-baseline gap-3 flex-wrap">
            {hrv != null && (
              <span className="flex items-baseline gap-1.5">
                <span
                  data-testid="today-hrv-value"
                  className="text-[17px] font-bold tabular-nums leading-none text-fg-primary"
                >
                  {Math.round(hrv)}
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-fg-muted">HRV</span>
                {hrvDelta != null && (
                  <span
                    className="text-[9px] uppercase tracking-[0.14em] font-bold"
                    style={{ color: hrvDelta >= 0 ? "var(--ok)" : "var(--danger-500)" }}
                  >
                    {hrvDelta >= 0 ? "+" : ""}
                    {hrvDelta}
                  </span>
                )}
              </span>
            )}
            {sleepHours != null && (
              <span className="flex items-baseline gap-1.5">
                <span
                  data-testid="today-sleep-value"
                  className="text-[17px] font-bold tabular-nums leading-none text-fg-primary"
                >
                  {sleepHours.toFixed(1)}
                  {t("hero.hours_suffix", { defaultValue: "ч" })}
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-fg-muted">
                  {t("readiness.sub_sleep", { defaultValue: "Сон" })}
                </span>
              </span>
            )}
          </div>
          {subSignals && subSignals.length > 0 && (
            <div className="flex items-center gap-2.5 flex-wrap">
              {subSignals.map((s) => (
                <span key={s.label} className="flex items-center gap-1">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: TONE_COLOR[s.tone === "muted" ? "muted" : s.tone] }}
                  />
                  <span className="text-[10px] text-fg-secondary">{s.label}</span>
                </span>
              ))}
            </div>
          )}
          {!checkedInToday && (
            <Link
              to="/wellness/checkin"
              data-testid="today-checkin-cta"
              className="self-start inline-flex items-center px-2.5 py-1 rounded-tile bg-accent/15 ring-1 ring-inset ring-accent/40 text-accent text-[10.5px] font-semibold hover:bg-accent/25 transition-colors"
            >
              {t("checkin.cta", { defaultValue: "Чек-ин · 20 секунд" })}
            </Link>
          )}
        </div>
      </div>

      {/* ── Тренировка дня ── */}
      <div className="border-t border-bg-border/60 pt-3.5" data-testid="hero-workout">
        {workout == null ? (
          <div data-testid="hero-workout-rest">
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
              {t("hero.rest_eyebrow", { defaultValue: "Сегодня" })}
            </div>
            <div className="mt-1.5 text-[19px] font-bold text-fg-primary tracking-[-0.02em] font-display">
              {t("hero.rest_title", { defaultValue: "День отдыха" })}
            </div>
            <div className="mt-1 text-[12px] text-fg-secondary">
              {t("hero.rest_hint", { defaultValue: "Восстановление — тоже тренировка. Сон и еда сегодня главные." })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: dot }} />
              <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
                {isDone
                  ? t("hero.session_done_eyebrow", { defaultValue: "Сессия · выполнена" })
                  : t("session.heading", { defaultValue: "Сессия" })}
              </span>
            </div>
            <button
              type="button"
              data-testid="hero-workout-title"
              onClick={onOpenWorkout}
              className="mt-1.5 text-left text-[24px] font-bold text-fg-primary tracking-[-0.025em] leading-[1.1] font-display"
            >
              {workout.title ?? t("default_workout_name", { defaultValue: "Тренировка" })}
            </button>
            <div className="mt-1.5 flex items-center gap-2 text-[12.5px] text-fg-secondary tabular-nums flex-wrap">
              {duration && <span>{duration}</span>}
              {duration && distance && <span className="text-fg-muted">·</span>}
              {distance && (
                <span>
                  {distance} {t("unit_km", { defaultValue: "км" })}
                </span>
              )}
              {workout.planned_load != null && (
                <>
                  <span className="text-fg-muted">·</span>
                  <span>TSS {workout.planned_load}</span>
                </>
              )}
            </div>
            {!isDone && onStart && (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  data-testid="hero-workout-start"
                  onClick={onStart}
                  aria-label={t("session.cta_start_aria", { defaultValue: "Запустить тренировку" })}
                  className="flex-1 px-0 py-2.5 rounded-md bg-accent text-white font-semibold text-sm hover:brightness-110 transition"
                >
                  {t("session.cta_start", { defaultValue: "Запустить" })}
                </button>
                {onOpenWorkout && (
                  <button
                    type="button"
                    data-testid="hero-workout-open"
                    onClick={onOpenWorkout}
                    className="px-3.5 py-2.5 rounded-md border border-bg-border text-fg-secondary text-[13px] hover:bg-bg-raised transition"
                  >
                    {t("hero.cta_details", { defaultValue: "Детали" })}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {children}
      </div>
    </div>
  );
}
